<template>
    <div class="rating-chart">
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                </div>
            </div>
            <div class="right"></div>
        </div>
        <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        <StackedAreaChart
            :title="$t('report.ratingTimeSeries')"
            :x-axis-data="getXAxisData()"
            :legends="legends"
            :colors="colors"
            :series="getSeries()"
            v-else
        />
        <div class="stats">
            <StatCard
                :title="$t('report.totalRatingCount')"
                :value="stats.totalRatingCount"
                :unit="$t('report.rate')"
            />
            <StatCard
                :title="getTitle('averageRating')"
                :value="formatStat(stats.AverageRating).value"
                :unit="formatStat(stats.AverageRating).unit"
            />
            <StatCard
                :title="getTitle('weightedAverageRating')"
                :value="formatStat(stats.WeightedAverageRating).value"
                :unit="formatStat(stats.WeightedAverageRating).unit"
            />
        </div>
        <div class="stats">
            <StatCard
                v-if="stats.totalRatingCount > 0"
                :key="key"
                :title="key"
                :value="stats.ratingCounts[key]"
                v-for="key of Object.keys(stats.ratingCounts)"
            />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import * as echarts from "echarts/lib/echarts";
import _ from "lodash";
import { expertToExcel } from "../../../utils/utils";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import StackedAreaChart from "../../Pages/Chatbot/pages/reports/charts/StackedAreaChart.vue";
import { REPORT_API } from "../../../api/statistics";
import log2 from "../../../utils/log2";

const log = log2("OverallRatingChart");
export default Vue.extend({
    name: "OverallRatingChart",
    components: { TableView, IconButton, StatCard, StackedAreaChart },
    props: {
        filters: {
            type: Object,
            default: () => ({
                users: [],
                channels: [],
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
    },
    data() {
        return {
            colors: ["#80FFA5", "#00DDFF", "#37A2FF", "#FF0087", "#FFBF00", "#ec3131"],
            legends: [this.$t("report.notReport"), "⭐", "⭐⭐", "⭐⭐⭐", "⭐⭐⭐⭐", "⭐⭐⭐⭐⭐"],
            tableMode: false,
            loading: true,
            graphData: {
                buckets: [],
                timeSeriesType: "hour",
            },
            loadingInstance: null,
            stats: this.getStats(),
        };
    },
    methods: {
        getTitle(type) {
            const hourlyText = this.$t("report.hourly");
            if (this.getStats().isHourlyAverage) {
                return `${this.$t(`report.${type}`)} ${hourlyText}`;
            }
            return this.$t(`report.${type}`);
        },
        formatStat(value) {
            if (this.stats.totalRatingCount < 1) {
                return {
                    value: this.$t("report.noEnoughtData"),
                    unit: "",
                };
            }
            return {
                value: value,
                unit: this.$t("message.point"),
            };
        },
        fetch() {
            this.loading = true;
            this.loadingInstance.show();
            REPORT_API.ratingOverall(this.filters)
                .then((res) => {
                    log("ratingOverall data fetched", res);
                    this.graphData = res;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        getCols() {
            return [
                {
                    title: this.$t("chatbotv2.date"),
                    dataIndex: "date",
                    key: "date",
                },
                ...["Not Rated", "⭐", "⭐⭐", "⭐⭐⭐", "⭐⭐⭐⭐", "⭐⭐⭐⭐⭐"].map((legend) => ({
                    title: legend,
                    dataIndex: legend,
                    key: legend,
                })),
            ];
        },
        getRows() {
            const { buckets, timeSeriesType } = this.graphData;
            const rows = buckets.map((bucket) => {
                const rates = {
                    "Not Rated": bucket.noRating,
                    "⭐": bucket.oneStar,
                    "⭐⭐": bucket.twoStar,
                    "⭐⭐⭐": bucket.threeStar,
                    "⭐⭐⭐⭐": bucket.fourStar,
                    "⭐⭐⭐⭐⭐": bucket.fiveStar,
                };

                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];
                return {
                    date,
                    ...rates,
                };
            });
            return rows;
        },
        getStats() {
            const startDate = moment(this.filters.startDate);
            const endDate = moment(this.filters.endDate);
            const buckets = this.graphData?.buckets || [];
            const totalRatingCount = _.sumBy(buckets, (bucket) => bucket.totalRatingCount);
            const sumOfRatings = _.sumBy(
                buckets,
                (bucket) =>
                    bucket.oneStar * 1 +
                    bucket.twoStar * 2 +
                    bucket.threeStar * 3 +
                    bucket.fourStar * 4 +
                    bucket.fiveStar * 5
            );
            let AverageRating = totalRatingCount ? sumOfRatings / totalRatingCount : 0;
            AverageRating = parseFloat(AverageRating.toFixed(2));
            const WeightedAverageRating = AverageRating;

            const ratingCounts = buckets.reduce(
                (acc, bucket) => {
                    acc["Not Rated"] += bucket.noRating;
                    acc["⭐"] += bucket.oneStar;
                    acc["⭐⭐"] += bucket.twoStar;
                    acc["⭐⭐⭐"] += bucket.threeStar;
                    acc["⭐⭐⭐⭐"] += bucket.fourStar;
                    acc["⭐⭐⭐⭐⭐"] += bucket.fiveStar;
                    return acc;
                },
                {
                    "Not Rated": 0,
                    "⭐": 0,
                    "⭐⭐": 0,
                    "⭐⭐⭐": 0,
                    "⭐⭐⭐⭐": 0,
                    "⭐⭐⭐⭐⭐": 0,
                }
            );

            return {
                totalRatingCount,
                AverageRating,
                WeightedAverageRating,
                ratingCounts,
                isHourlyAverage: startDate.isSame(endDate, "day"),
            };
        },
        getXAxisData() {
            const { buckets, timeSeriesType } = this.graphData;
            log("getXAxisData.buckets", buckets);
            return buckets.map((item) => {
                if (timeSeriesType === "day") {
                    return moment(item.date).format("DD/MM");
                } else if (timeSeriesType === "hour") {
                    return moment(item.date).format("HH:mm");
                } else if (timeSeriesType === "week") {
                    return moment(item.date).format("YYYY-WW[week]");
                } else {
                    //monthly
                    return moment(item.date).format("MMM");
                }
            });
        },
        getSeries() {
            const defaultColor1 = "rgb(200, 200, 200)";
            const { buckets, timeSeriesType } = this.graphData;

            const gradients = {
                "Not Rated": {
                    color1: "rgb(128, 255, 165)",
                    color2: "rgb(1, 191, 236)",
                },
                "⭐": {
                    color1: "rgb(0, 221, 255)",
                    color2: "rgb(77, 119, 255)",
                },
                "⭐⭐": {
                    color1: "rgb(55, 162, 255)",
                    color2: "rgb(116, 21, 219)",
                },
                "⭐⭐⭐": {
                    color1: "rgb(255, 0, 135)",
                    color2: "rgb(135, 0, 157)",
                },
                "⭐⭐⭐⭐": {
                    color1: "rgb(255, 191, 0)",
                    color2: "rgb(224, 62, 76)",
                },
                "⭐⭐⭐⭐⭐": {
                    color1: "rgb(236, 49, 49)",
                    color2: "rgb(255, 0, 0)",
                },
            };
            const ratingMap = {
                "Not Rated": "noRating",
                "⭐": "oneStar",
                "⭐⭐": "twoStar",
                "⭐⭐⭐": "threeStar",
                "⭐⭐⭐⭐": "fourStar",
                "⭐⭐⭐⭐⭐": "fiveStar",
            };
            const series = this.legends.map((legend, i) => {
                const data = buckets.map((bucket) => {
                    return bucket[ratingMap[legend]];
                });

                log("getSeries.data", data);
                const seriesData = {
                    name: legend,
                    type: "line",
                    stack: "Total",
                    smooth: true,
                    lineStyle: {
                        width: 0,
                    },
                    showSymbol: false,
                    areaStyle: {
                        opacity: 0.8,
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            {
                                offset: 0,
                                color: gradients[legend]?.color1 || defaultColor1,
                            },
                            {
                                offset: 1,
                                color: gradients[legend]?.color2 || "defaultColor2",
                            },
                        ]),
                    },
                    emphasis: {
                        focus: "series",
                    },
                    data,
                };
                return seriesData;
            });
            return series;
        },
        exportToExcel() {
            const { buckets, timeSeriesType } = this.graphData;
            const data = buckets.map((bucket) => {
                const rates = bucket.items.reduce(
                    (acc, item) => {
                        if (!item.point) {
                            acc["Not Rated"] += 1;
                        } else if (item.point === 1) {
                            acc["⭐"] += 1;
                        } else if (item.point === 2) {
                            acc["⭐⭐"] += 1;
                        } else if (item.point === 3) {
                            acc["⭐⭐⭐"] += 1;
                        } else if (item.point === 4) {
                            acc["⭐⭐⭐⭐"] += 1;
                        } else if (item.point === 5) {
                            acc["⭐⭐⭐⭐⭐"] += 1;
                        }
                        return acc;
                    },
                    {
                        "Not Rated": 0,
                        "⭐": 0,
                        "⭐⭐": 0,
                        "⭐⭐⭐": 0,
                        "⭐⭐⭐⭐": 0,
                        "⭐⭐⭐⭐⭐": 0,
                    }
                );

                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];

                return {
                    date,
                    ...rates,
                };
            });
            const stats = this.getStats();
            const excelData = [
                ...data,
                {
                    date: "Toplam Derecelendirme Sayısı",
                    ...stats.ratingCounts,
                },
                {
                    date: "Ortalama puanı",
                    "Average Rating": stats.AverageRating,
                },
                {
                    date: "Ağırlıklı Ortalama Not",
                    "Weighted Average Rating": stats.WeightedAverageRating,
                },
            ];
            expertToExcel(excelData, `rating_timeseries.xlsx`, "Sheet 1");
        },
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".rating-chart"),
            name: "wave",
        });
        this.fetch();
    },
    watch: {
        filters: {
            handler: function (val, oldVal) {
                this.fetch();
            },
            deep: true,
        },
        graphData: {
            handler: function (val, oldVal) {
                this.stats = this.getStats();
            },
            deep: true,
        },
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
    },
});
</script>

<style scoped lang="scss">
.rating-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
